import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

const PricingCard = ({ plan, index }) => {
  const isPro = plan.popular;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative"
    >
      {isPro && (
        <motion.div
          className="absolute -top-4 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="px-4 py-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold rounded-full flex items-center gap-2">
            <Zap className="w-3 h-3" />
            Most Popular
          </div>
        </motion.div>
      )}

      <motion.div
        whileHover={{ y: isPro ? -12 : -8, scale: isPro ? 1.03 : 1.02 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className={`relative h-full p-8 rounded-2xl border transition-all duration-500 overflow-hidden ${
          isPro
            ? 'bg-gradient-to-br from-[#1a1a1b] via-[#0f0f10] to-[#1a1a1b] border-cyan-500/30 shadow-lg shadow-cyan-500/10'
            : 'bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] border-cyan-500/10 hover:border-cyan-500/20'
        }`}
      >
        {/* Glow effect */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: isPro
              ? 'radial-gradient(circle at top, rgba(0, 217, 255, 0.1), transparent 60%)'
              : 'radial-gradient(circle at top, rgba(0, 217, 255, 0.05), transparent 60%)'
          }}
        />

        <div className="relative z-10">
          {/* Plan name */}
          <h3 className="text-2xl font-bold text-white mb-2 text-balance">{plan.name}</h3>
          <p className="text-gray-400 text-sm mb-6 min-h-[40px] text-pretty">{plan.description}</p>

          {/* Price */}
          <div className="mb-8">
            {plan.price === 'Sur devis' || plan.price === 'Custom quote' ? (
              <div className="text-4xl font-bold text-white">{plan.price}</div>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  {plan.price}
                </span>
                <span className="text-xl text-gray-400">{plan.currency}</span>
                {plan.period && <span className="text-gray-500">/ {plan.period}</span>}
              </div>
            )}
          </div>

          {/* Features */}
          <ul className="space-y-4 mb-8">
            {plan.features.map((feature, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * i }}
                className="flex items-start gap-3"
              >
                <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-cyan-400" />
                </div>
                <span className="text-gray-300 text-sm leading-relaxed text-pretty">{feature}</span>
              </motion.li>
            ))}
          </ul>

          {/* CTA Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`w-full px-6 py-4 rounded-lg font-semibold transition-all duration-300 ${
              isPro
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40'
                : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20'
            }`}
          >
            {plan.cta}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Pricing = () => {
  const { language } = useLanguage();
  const [pricing, setPricing] = useState([]);

  useEffect(() => {
    api.getPricing().then(response => {
      // Transform pricing to match component structure
      const list = Array.isArray(response.data) ? response.data : [];
      const transformed = list.map(p => ({
        ...p,
        price: language === 'en' && p.priceEn ? p.priceEn : p.price,
        period: language === 'en' && p.periodEn ? p.periodEn : p.period,
        description: language === 'en' && p.descriptionEn ? p.descriptionEn : p.description,
        features: (() => {
          const f = language === 'en' && p.featuresEn ? p.featuresEn : p.features;
          return Array.isArray(f) ? f : [];
        })(),
        cta: language === 'en' && p.ctaEn ? p.ctaEn : p.cta
      }));
      setPricing(transformed);
    }).catch(err => console.error('Error loading pricing:', err));
  }, [language]);

  return (
    <section id="pricing" className="relative py-32 bg-[#0a0a0b] overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-600/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6"
          >
            <span className="text-sm text-cyan-400 font-medium font-mono">
              {language === 'fr' ? 'TARIFS' : 'PRICING'}
            </span>
          </motion.div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 text-balance hyphens-none px-1">
            {language === 'fr' ? 'Des formules' : 'Pricing plans'}{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {language === 'fr' ? 'adaptées' : 'that scale'}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto text-pretty px-1">
            {language === 'fr'
              ? 'Choisissez la formule qui correspond à vos besoins et à votre budget'
              : 'Choose the plan that fits your needs and budget'
            }
          </p>
        </motion.div>

        {/* Pricing grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricing.map((plan, index) => (
            <PricingCard key={plan.id} plan={plan} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
